import React, { useState, useEffect } from 'react';
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  Upload,
  Check,
  FileText,
  Settings,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Use cookie-based auth - credentials: 'include' sends session cookie
const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

// Helper to make authenticated fetch requests
const authFetch = (url, options = {}) => {
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });
};

const STEPS = [
  { id: 1, title: 'Select Vendor', icon: Settings },
  { id: 2, title: 'Upload File', icon: Upload },
  { id: 3, title: 'Configure', icon: FileText },
];

const CreateImportWizard = ({ onBack, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Step 1: Vendor selection
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [mappingProfiles, setMappingProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  
  // Step 2: File upload
  const [sourceKind, setSourceKind] = useState('csv');
  const [file, setFile] = useState(null);
  const [importId, setImportId] = useState(null);
  
  // Step 3: Options
  const [options, setOptions] = useState({
    allow_missing_images: false,
    default_product_type_id: '',
    default_category_id: '',
    auto_match_mode: 'exact_only',
    publish_mode: 'upsert'
  });
  
  // Metadata
  const [productTypes, setProductTypes] = useState([]);
  const [categories, setCategories] = useState([]);

  // Fetch vendors on mount
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/vendors?is_active=true`);
        if (resp.ok) {
          const data = await resp.json();
          setVendors(data.vendors || []);
        }
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
      }
    };
    fetchVendors();
  }, []);

  // Fetch mapping profiles when vendor selected
  useEffect(() => {
    if (!selectedVendor) {
      setMappingProfiles([]);
      setSelectedProfile(null);
      return;
    }
    
    const fetchProfiles = async () => {
      try {
        const resp = await authFetch(`${API_URL}/api/catalog-imports/admin/vendors/${selectedVendor.id}/mapping-profiles`);
        if (resp.ok) {
          const data = await resp.json();
          setMappingProfiles(data.profiles || []);
          // Auto-select vendor's default profile
          if (selectedVendor.field_mapping_profile_id) {
            const defaultProfile = data.profiles?.find(p => p.id === selectedVendor.field_mapping_profile_id);
            if (defaultProfile) setSelectedProfile(defaultProfile);
          }
        }
      } catch (err) {
        console.error('Failed to fetch profiles:', err);
      }
    };
    fetchProfiles();
  }, [selectedVendor]);

  // Fetch metadata for step 3
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [ptResp, catResp] = await Promise.all([
          authFetch(`${API_URL}/api/product-types`),
          authFetch(`${API_URL}/api/categories?limit=500`)
        ]);
        
        if (ptResp.ok) {
          const ptData = await ptResp.json();
          setProductTypes(ptData.product_types || []);
        }
        if (catResp.ok) {
          const catData = await catResp.json();
          setCategories(catData.categories || []);
        }
      } catch (err) {
        console.error('Failed to fetch metadata:', err);
      }
    };
    fetchMetadata();
  }, []);

  const handleNext = async () => {
    setError(null);
    
    if (step === 1) {
      if (!selectedVendor) {
        setError('Please select a vendor');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!file) {
        setError('Please upload a file');
        return;
      }
      
      try {
        setLoading(true);
        
        // Create import
        const createResp = await authFetch(`${API_URL}/api/catalog-imports/admin/imports`, {
          method: 'POST',
          body: JSON.stringify({
            vendor_id: selectedVendor.id,
            source_kind: sourceKind,
            mapping_profile_id: selectedProfile?.id || null,
            options: {
              ...options,
              default_product_type_id: options.default_product_type_id || selectedVendor.default_product_type_id || null
            }
          })
        });
        
        if (!createResp.ok) {
          const errData = await createResp.json();
          throw new Error(errData.detail || 'Failed to create import');
        }
        
        const createData = await createResp.json();
        const newImportId = createData.import.id;
        setImportId(newImportId);
        
        // Upload file - use FormData (no Content-Type header needed)
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadResp = await fetch(`${API_URL}/api/catalog-imports/admin/imports/${newImportId}/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        
        if (!uploadResp.ok) {
          const errData = await uploadResp.json();
          throw new Error(errData.detail || 'Failed to upload file');
        }
        
        setStep(3);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    } else if (step === 3) {
      // Complete - go to import detail
      onComplete(importId);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Determine source kind from file extension
      const ext = selectedFile.name.split('.').pop()?.toLowerCase();
      if (ext === 'csv') {
        setSourceKind('csv');
      } else if (ext === 'xlsx' || ext === 'xls') {
        setSourceKind('xlsx');
      }
      setFile(selectedFile);
    }
  };

  return (
    <div className="space-y-6" data-testid="create-import-wizard">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h2 className="text-lg font-heading font-semibold text-ivory-800">
            Create New Import
          </h2>
          <p className="text-sm text-ivory-500">Upload vendor catalog data</p>
        </div>
      </div>

      {/* Steps Indicator */}
      <div className="flex items-center justify-center gap-4">
        {STEPS.map((s, idx) => (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-2 ${step >= s.id ? 'text-gold-600' : 'text-ivory-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step > s.id ? 'bg-green-500 text-white' :
                step === s.id ? 'bg-gold-500 text-white' : 'bg-ivory-200'
              }`}>
                {step > s.id ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
              </div>
              <span className="text-sm font-medium">{s.title}</span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`w-12 h-0.5 ${step > s.id ? 'bg-green-500' : 'bg-ivory-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Step Content */}
      <div className="bg-white rounded-xl border border-ivory-200 p-6">
        {/* Step 1: Select Vendor */}
        {step === 1 && (
          <div className="space-y-6">
            <h3 className="font-semibold text-ivory-800">Select Vendor Source</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {vendors.map(vendor => (
                <div
                  key={vendor.id}
                  onClick={() => setSelectedVendor(vendor)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedVendor?.id === vendor.id
                      ? 'border-gold-500 bg-gold-50 ring-2 ring-gold-200'
                      : 'border-ivory-200 hover:border-ivory-300'
                  }`}
                  data-testid={`vendor-option-${vendor.id}`}
                >
                  <p className="font-medium text-ivory-800">{vendor.name}</p>
                  <p className="text-xs text-ivory-500">{vendor.code}</p>
                  {vendor.imports_count > 0 && (
                    <p className="text-xs text-ivory-400 mt-1">{vendor.imports_count} imports</p>
                  )}
                </div>
              ))}
            </div>

            {vendors.length === 0 && (
              <p className="text-center text-ivory-500 py-8">
                No vendor sources found. Create one first in the Vendors page.
              </p>
            )}

            {selectedVendor && mappingProfiles.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-ivory-700 mb-3">Select Mapping Profile</h4>
                <div className="space-y-2">
                  {mappingProfiles.map(profile => (
                    <div
                      key={profile.id}
                      onClick={() => setSelectedProfile(profile)}
                      className={`p-3 border rounded-lg cursor-pointer ${
                        selectedProfile?.id === profile.id
                          ? 'border-gold-500 bg-gold-50'
                          : 'border-ivory-200 hover:border-ivory-300'
                      }`}
                    >
                      <p className="font-medium text-sm">{profile.name}</p>
                      <p className="text-xs text-ivory-500">{profile.source_kind.toUpperCase()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Upload File */}
        {step === 2 && (
          <div className="space-y-6">
            <h3 className="font-semibold text-ivory-800">Upload Catalog File</h3>
            
            <div className="bg-ivory-50 rounded-lg p-4">
              <p className="text-sm text-ivory-600 mb-2">
                <strong>Vendor:</strong> {selectedVendor?.name}
              </p>
              {selectedProfile && (
                <p className="text-sm text-ivory-600">
                  <strong>Mapping Profile:</strong> {selectedProfile.name}
                </p>
              )}
            </div>

            <div 
              className="border-2 border-dashed border-ivory-300 rounded-lg p-8 text-center hover:border-gold-400 transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const droppedFile = e.dataTransfer.files?.[0];
                if (droppedFile) {
                  handleFileChange({ target: { files: [droppedFile] } });
                }
              }}
            >
              {file ? (
                <div className="space-y-2">
                  <FileText className="w-12 h-12 mx-auto text-gold-500" />
                  <p className="font-medium text-ivory-800">{file.name}</p>
                  <p className="text-sm text-ivory-500">
                    {(file.size / 1024).toFixed(1)} KB • {sourceKind.toUpperCase()}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setFile(null)}>
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-12 h-12 mx-auto text-ivory-400" />
                  <p className="text-ivory-600">Drag & drop your file here, or click to browse</p>
                  <p className="text-xs text-ivory-400">Supported: CSV, XLSX</p>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <Button variant="outline" onClick={() => document.getElementById('file-upload').click()}>
                    Browse Files
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Configure Options */}
        {step === 3 && (
          <div className="space-y-6">
            <h3 className="font-semibold text-ivory-800">Import Options</h3>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-700 flex items-center gap-2">
                <Check className="w-4 h-4" />
                File uploaded successfully! Configure options below.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-2">
                  Default Product Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-ivory-300 rounded-lg"
                  value={options.default_product_type_id}
                  onChange={(e) => setOptions({ ...options, default_product_type_id: e.target.value })}
                >
                  <option value="">None (use mapping)</option>
                  {productTypes.map(pt => (
                    <option key={pt.id} value={pt.id}>{pt.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-2">
                  Default Category
                </label>
                <select
                  className="w-full px-3 py-2 border border-ivory-300 rounded-lg"
                  value={options.default_category_id}
                  onChange={(e) => setOptions({ ...options, default_category_id: e.target.value })}
                >
                  <option value="">None (use mapping)</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-2">
                  Publish Mode
                </label>
                <select
                  className="w-full px-3 py-2 border border-ivory-300 rounded-lg"
                  value={options.publish_mode}
                  onChange={(e) => setOptions({ ...options, publish_mode: e.target.value })}
                >
                  <option value="upsert">Upsert (Create or Update)</option>
                  <option value="create_only">Create Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ivory-700 mb-2">
                  Auto Match Mode
                </label>
                <select
                  className="w-full px-3 py-2 border border-ivory-300 rounded-lg"
                  value={options.auto_match_mode}
                  onChange={(e) => setOptions({ ...options, auto_match_mode: e.target.value })}
                >
                  <option value="exact_only">Exact Match Only</option>
                  <option value="off">Off</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="allow-missing-images"
                checked={options.allow_missing_images}
                onChange={(e) => setOptions({ ...options, allow_missing_images: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="allow-missing-images" className="text-sm text-ivory-700">
                Allow missing images (show warning instead of error)
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => step > 1 ? setStep(step - 1) : onBack()}
          disabled={loading}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step === 1 ? 'Cancel' : 'Back'}
        </Button>
        
        <Button 
          onClick={handleNext} 
          disabled={loading}
          data-testid="wizard-next-btn"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {step === 3 ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Go to Import
            </>
          ) : (
            <>
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default CreateImportWizard;
